import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Trash2, Edit, Save, X, RefreshCw, ChevronRight } from "lucide-react";

import type { MaintenanceLookup } from "./types";

interface Props {
  table: "maintenance_systems" | "maintenance_work_types";
  title: string;
  description: string;
  icon: React.ReactNode;
  placeholder: string;
  onChanged?: () => void;
}

const MaintenanceListManager: React.FC<Props> = ({ table, title, description, icon, placeholder, onChanged }) => {
  const [rows, setRows] = useState<MaintenanceLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("100");
  const [open, setOpen] = useState(false);


  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from(table).select("*").order("sort_order").order("name");
    setLoading(false);
    if (error) {
      toast({ title: "Load failed", description: error.message, variant: "destructive" });
      return;
    }
    setRows((data || []) as MaintenanceLookup[]);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const reset = () => {
    setEditId(null);
    setName("");
    setSortOrder("100");
  };

  const startEdit = (r: MaintenanceLookup) => {
    setEditId(r.id);
    setName(r.name);
    setSortOrder(String(r.sort_order ?? 100));
  };

  const save = async () => {
    if (!name.trim()) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { name: name.trim(), sort_order: Number(sortOrder) || 100, is_active: true };
    const q = editId
      ? supabase.from(table).update(payload).eq("id", editId).select()
      : supabase.from(table).insert([payload]).select();
    const { error } = await q;
    setSaving(false);
    if (error) {
      toast({ title: "Not saved", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Saved", description: `"${payload.name}" saved.` });
    reset();
    await load();
    onChanged?.();
  };

  const toggleActive = async (r: MaintenanceLookup) => {
    const { error } = await supabase.from(table).update({ is_active: !r.is_active }).eq("id", r.id);
    if (error) {
      toast({ title: "Not updated", description: error.message, variant: "destructive" });
      return;
    }
    await load();
    onChanged?.();
  };

  const remove = async (r: MaintenanceLookup) => {
    if (!window.confirm(`Delete "${r.name}"?\n\nExisting maintenance records keep their text value, but it will no longer be selectable.`)) return;
    const { error } = await supabase.from(table).delete().eq("id", r.id);
    if (error) {
      toast({ title: "Not deleted", description: error.message, variant: "destructive" });
      return;
    }
    await load();
    onChanged?.();
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className={open ? "border-b border-border" : ""}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-start gap-3 text-left group"
          >
            <ChevronRight
              className={`w-5 h-5 mt-0.5 text-muted-foreground transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
            />
            <div>
              <CardTitle className="text-lg text-primary flex items-center gap-2">
                {icon}
                {title}
                {!open && <span className="text-xs font-normal text-muted-foreground">({rows.length})</span>}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            </div>
          </button>
          {open && (
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          )}
        </div>
      </CardHeader>

      {open && (
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col sm:flex-row items-end gap-3 rounded-xl border border-yellow-900/30 bg-black/20 p-4">
          <div className="flex-1 w-full">
            <Label className="text-yellow-500">{editId ? "Edit name" : "New name"} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} />
          </div>
          <div className="w-full sm:w-28">
            <Label className="text-yellow-500">Sort</Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </div>
          <div className="flex gap-2">
            {editId && (
              <Button variant="outline" onClick={reset}>
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button onClick={save} disabled={saving} className="golden-button">
              {editId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editId ? "Update" : "Add"}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Sort</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No items yet. Add one above.</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className={`border-b border-border last:border-b-0 ${!r.is_active ? "opacity-60" : ""}`}>
                    <td className="px-3 py-3 font-semibold text-foreground">{r.name}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{r.sort_order}</td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(r)}
                        className={`rounded-full border px-2 py-1 text-xs font-medium ${
                          r.is_active
                            ? "border-green-700/50 bg-green-900/30 text-green-300"
                            : "border-gray-700 bg-gray-900/40 text-gray-400"
                        }`}
                      >
                        {r.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(r)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => remove(r)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      )}
    </Card>
  );
};

export default MaintenanceListManager;
